import React, { useEffect } from 'react';

import { createPortal } from 'react-dom';

import { X } from 'lucide-react';

import Button from './Button';
import Typography from './Typography';

export interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
	closeOnOverlayClick?: boolean;
	showCloseButton?: boolean;
	children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
	isOpen,
	onClose,
	title,
	size = 'md',
	closeOnOverlayClick = true,
	showCloseButton = true,
	children,
}) => {
	// Handle escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen) {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener('keydown', handleEscape);
			// Prevent body scroll
			document.body.style.overflow = 'hidden';
		}

		return () => {
			document.removeEventListener('keydown', handleEscape);
			document.body.style.overflow = 'unset';
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	// Size styles
	const sizeStyles = {
		sm: 'max-w-md',
		md: 'max-w-lg',
		lg: 'max-w-2xl',
		xl: 'max-w-4xl',
		full: 'max-w-7xl',
	};

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget && closeOnOverlayClick) {
			onClose();
		}
	};

	const modalContent = (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 m-0">
			<div className="fixed inset-0 m-0" onClick={handleOverlayClick} />
			<div
				className={`
          relative bg-white rounded-lg
          shadow-xl w-full font-primary
          ${sizeStyles[size]} max-h-[90vh] overflow-hidden
        `}
			>
				{(title || showCloseButton) && (
					<div className="flex items-center justify-between p-6 border-b border-neutral-200">
						{title && (
							<Typography variant="h4" color="inherit">
								{title}
							</Typography>
						)}
						{showCloseButton && (
							<Button
								variant="ghost"
								size="sm"
								onClick={onClose}
								icon={X}
								aria-label="Close modal"
							/>
						)}
					</div>
				)}

				<div className="overflow-y-auto max-h-[calc(90vh-120px)]">
					{children}
				</div>
			</div>
		</div>
	);

	// Render modal in a portal to avoid parent spacing utilities affecting it
	return typeof document !== 'undefined'
		? createPortal(modalContent, document.body)
		: modalContent;
};

export default Modal;

// Modal content components for better structure
export const ModalHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
	className = '',
	children,
	...props
}) => (
	<div
		className={`p-6 border-b border-neutral-200 font-primary ${className}`}
		{...props}
	>
		{children}
	</div>
);

export const ModalBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
	className = '',
	children,
	...props
}) => (
	<div className={`p-6 font-primary ${className}`} {...props}>
		{children}
	</div>
);

export const ModalFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
	className = '',
	children,
	...props
}) => (
	<div
		className={`p-6 border-t border-neutral-200 flex justify-end space-x-3 font-primary ${className}`}
		{...props}
	>
		{children}
	</div>
);
